import { fill } from 'lodash';

import { __ } from '../../../i18n/l10n';
import { BetslipEntity } from '../../../states/entities/betslips';
import { BetslipDisplayStatuses } from '../../../types/betslip';
import type { BetslipDisplayStatus } from '../../../types/betslip';
import { COST_PER_GRID } from '../constants';
import { GridStatuses } from '../types';
import { GridMatchLengths } from '../types';
import type { GridState, GridsPoolState, GridMatchLength } from '../types';

import Selection from './selection';

class Grid {
  static getLink = (gridReference: string): string => `/grilles/history/${gridReference}`;

  static getBetslipStatus = (grid: GridState): BetslipDisplayStatus | null => {
    const { betslips, gridStatus, allWinningsAmount } = grid;
    const betslip = betslips && betslips[0]; // FIXME check if we can use first betslip status
    if (betslip) {
      const statusDisplayBetslip = BetslipEntity.getDisplayStatus(betslip.betslipStatus, allWinningsAmount);

      switch (gridStatus) {
        case GridStatuses.RUNNING: {
          return BetslipDisplayStatuses.RUNNING;
        }
        case GridStatuses.FUNDING: {
          return BetslipDisplayStatuses.RUNNING;
        }
        case GridStatuses.NOT_FUNDED: {
          return BetslipDisplayStatuses.CANCELLED;
        }
        case GridStatuses.SETTLED: {
          // @ts-ignore
          return statusDisplayBetslip;
        }
        default:
          return null;
      }
    }
    return null;
  };

  static isFunding = (grid: GridState | null): boolean => {
    if (grid) {
      const { gridStatus } = grid;
      return gridStatus === GridStatuses.FUNDING;
    }
    return false;
  };

  static isRunning = (grid: GridState | null): boolean => {
    if (grid) {
      const { gridStatus } = grid;
      return gridStatus === GridStatuses.RUNNING;
    }
    return false;
  };

  static isWinning = (grid: GridState | null): boolean => {
    if (grid) {
      const betslipStatus = Grid.getBetslipStatus(grid);
      return betslipStatus === BetslipDisplayStatuses.WINNING;
    }
    return false;
  };

  static isCancelled = (grid: GridState | null): boolean => {
    if (grid) {
      const betslipStatus = Grid.getBetslipStatus(grid);
      return betslipStatus === BetslipDisplayStatuses.CANCELLED;
    }
    return false;
  };

  static getBetslipStatusLabel = (grid: GridState) => {
    const { gridStatus } = grid;
    const betslipStatus = Grid.getBetslipStatus(grid);
    if (gridStatus === GridStatuses.RUNNING) {
      return __('!noun:GridsRunning');
    }
    if (gridStatus === GridStatuses.FUNDING) {
      return __('!noun:GridsFinancing');
    }
    if (gridStatus === GridStatuses.NOT_FUNDED) {
      return __('!noun:GridsCancelled');
    }
    if (gridStatus === GridStatuses.CREATING) {
      return __('!noun:GridsNotValidated');
    }
    if (gridStatus === GridStatuses.SETTLED) {
      switch (betslipStatus) {
        case BetslipDisplayStatuses.LOST:
          return __('!noun:GridsLost');
        case BetslipDisplayStatuses.CANCELLED:
          return __('!noun:GridsCancelled');
        case BetslipDisplayStatuses.WINNING:
          return __('!noun:GridsWon');
        default:
          return '';
      }
    }
    return '';
  };

  // Return array with index as grid score and value result
  // important strPoolResult is not inverse :(
  static getResults = (grid: GridState, pool: GridsPoolState): Array<number> => {
    const { matches, strPoolResult } = pool;
    const { strGridSelection, isSystem, systemStrGridSelections } = grid;
    const results = fill(Array(matches.length + 1), 0);
    if (!Selection.isEmpty(strPoolResult)) {
      const selections = isSystem && systemStrGridSelections ? systemStrGridSelections : [strGridSelection];
      const padResultsSelection = Selection.pad(strPoolResult);
      selections.forEach((selection) => {
        const padSelection = Selection.inverse(Selection.pad(selection));
        const individualSelections = Selection.getIndividualSelections(padSelection);
        individualSelections.forEach((individualSelection) => {
          const score = Selection.getScore(individualSelection, padResultsSelection);
          results[score] += 1;
        });
      });
    }
    return results;
  };

  static getAmount = (grid: GridState | null): number => {
    return grid ? grid.nbIndividualGrids * COST_PER_GRID : 0;
  };

  static isStakeTooHigh = (grid: GridState | null, betAmount: number): boolean => {
    if (grid && !isNaN(betAmount)) {
      const allBetAmount = grid?.allBetAmount || 0;
      const gridAmount = Grid.getAmount(grid);
      return betAmount > gridAmount - allBetAmount;
    }
    return false;
  };

  static getAuthorBadges = (grid: GridState | null, type: GridMatchLength | null): Array<number> => {
    if (grid && type) {
      const badges = grid.author?.badges;
      if (badges && badges.length) {
        const sortedBadges = [...badges].sort((a, b) => a - b);
        if (type === GridMatchLengths.SEVEN) {
          return sortedBadges ? sortedBadges.filter((badge) => badge <= 7) : [];
        } else {
          return sortedBadges ? sortedBadges.filter((badge) => badge <= 12 && badge > 7) : [];
        }
      }
    }
    return [];
  };

  static getBestAuthorBadge = (grid: GridState | null, type: GridMatchLength | null): number | null | undefined => {
    const badges = Grid.getAuthorBadges(grid, type);
    if (badges.length) {
      return badges[badges.length - 1];
    }
  };

  static getDate = (grid: GridState | null): number | null => {
    if (grid) {
      const { betslips } = grid;
      const firstBet = betslips && betslips[0];
      return firstBet?.validationDate;
    }
    return null;
  };
}

export default Grid;
